import { useMemo, useState } from 'react';
import { effectiveEventStatus, isEventOpenForPlay } from './eventOpen';

export const EVENT_KIND_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'match', label: 'Matches' },
  { id: 'poll', label: 'Polls' },
];

export const EVENT_STATUS_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'live', label: 'Live' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'locked', label: 'Locked' },
  { id: 'completed', label: 'Completed' },
  { id: 'all', label: 'All' },
];

export const EVENT_SORT_OPTIONS = [
  { id: 'soonest', label: 'Soonest' },
  { id: 'latest', label: 'Latest' },
  { id: 'az', label: 'A–Z' },
];

export function eventKind(item) {
  if (!item) return 'poll';
  if (item._kind === 'match' || item._kind === 'poll') return item._kind;
  if (item.question) return 'poll';
  if (item.teamA || item.teamB) return 'match';
  return 'poll';
}

export function eventTitle(item) {
  if (!item) return '';
  if (item.teamA && item.teamB) return `${item.teamA} vs ${item.teamB}`;
  return String(item.question || item.title || item.name || '');
}

export function eventCupSlug(item) {
  if (!item) return null;
  if (typeof item.cup === 'object' && item.cup?.slug) return item.cup.slug;
  return item.cupSlug || null;
}

export function eventCupName(item) {
  if (!item) return null;
  if (typeof item.cup === 'object' && item.cup?.name) return item.cup.name;
  return item.cupName || null;
}

export function eventTimestamp(item) {
  const t = new Date(item?.date || item?.createdAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function matchesStatusFilter(item, statusId) {
  if (!statusId || statusId === 'all') return true;
  if (statusId === 'open') return isEventOpenForPlay(item);
  const status = effectiveEventStatus(item);
  if (statusId === 'live') return status === 'live' || status === 'active';
  if (statusId === 'upcoming') return status === 'upcoming';
  if (statusId === 'locked') return status === 'locked';
  if (statusId === 'completed') return status === 'completed' || status === 'settled' || status === 'ended';
  return true;
}

export function cupsFromEvents(items = []) {
  const bySlug = new Map();
  for (const item of items) {
    const slug = eventCupSlug(item);
    const name = eventCupName(item);
    if (slug && !bySlug.has(slug)) bySlug.set(slug, { slug, name: name || slug });
  }
  return [...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function filterAndSortEvents(
  items = [],
  { kind = 'all', status = 'all', cup = 'all', query = '', sort = 'soonest' } = {}
) {
  const q = String(query || '')
    .trim()
    .toLowerCase();

  const filtered = items.filter((item) => {
    if (kind && kind !== 'all' && eventKind(item) !== kind) return false;
    if (!matchesStatusFilter(item, status)) return false;
    if (cup && cup !== 'all' && eventCupSlug(item) !== cup) return false;
    if (q) {
      const hay = [
        eventTitle(item),
        eventCupName(item),
        item.stageName,
        typeof item.stage === 'object' ? item.stage?.name : '',
        item.teamA,
        item.teamB,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'az') return eventTitle(a).localeCompare(eventTitle(b), undefined, { sensitivity: 'base' });
    const aDate = eventTimestamp(a);
    const bDate = eventTimestamp(b);
    if (sort === 'latest') return bDate - aDate;
    // soonest: open events first, then soonest date
    const aOpen = isEventOpenForPlay(a) ? 0 : 1;
    const bOpen = isEventOpenForPlay(b) ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return aDate - bDate;
  });

  return sorted;
}

export function defaultEventListFilters({ defaultStatus = 'open', defaultKind = 'all', defaultSort = 'soonest' } = {}) {
  return {
    kind: defaultKind,
    status: defaultStatus,
    cup: 'all',
    query: '',
    sort: defaultSort,
  };
}

export function eventListFiltersAreActive(filters, defaults) {
  if (!filters) return false;
  return (
    filters.kind !== defaults.kind ||
    filters.status !== defaults.status ||
    filters.cup !== defaults.cup ||
    filters.sort !== defaults.sort ||
    Boolean(String(filters.query || '').trim())
  );
}

export function useEventListFilters({
  defaultStatus = 'open',
  defaultKind = 'all',
  defaultSort = 'soonest',
} = {}) {
  const defaults = useMemo(
    () => defaultEventListFilters({ defaultStatus, defaultKind, defaultSort }),
    [defaultStatus, defaultKind, defaultSort]
  );
  const [filters, setFilters] = useState(defaults);

  const setKind = (kind) => setFilters((prev) => ({ ...prev, kind }));
  const setStatus = (status) => setFilters((prev) => ({ ...prev, status }));
  const setCup = (cup) => setFilters((prev) => ({ ...prev, cup }));
  const setSort = (sort) => setFilters((prev) => ({ ...prev, sort }));
  const setQuery = (query) => setFilters((prev) => ({ ...prev, query }));
  const reset = () => setFilters(defaults);

  return {
    filters,
    defaults,
    setKind,
    setStatus,
    setCup,
    setSort,
    setQuery,
    reset,
    isActive: eventListFiltersAreActive(filters, defaults),
  };
}
